import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  ViewChild
} from '@angular/core';
import {CyberSourceService} from '@app/payment/services/cybersource/cyber-source.service';
import {DcpCheckoutPaymentService} from '@app/payment/services/dcp-checkout-payment.service';
import {ActiveCartFacade, Cart} from '@spartacus/cart/base/root';
import {catchError, take} from 'rxjs/operators';
import {EMPTY, switchMap} from 'rxjs';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {isPlatformBrowser} from '@angular/common';
import {ScriptInjectionService} from '@app/config/script-injection.service';
import {DcpScriptInjection} from '@app/model/dcp-script-injection.model';
import {ConfigService, DcpGlobalMessageService} from '@dcp/configurator';
import {CaptureContext} from '@app/model/cyber-source/capture-context';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {PaymentDetails, TranslationService, User} from '@spartacus/core';
import {DcpCcPspService, PaymentProvider} from '@app/payment/services/dcp-cc-psp.service';
import {PayerAuthEnrollmentResponse} from '@app/model/cyber-source/payer-auth-enrollment-response';
import {TransientToken} from '@app/model/cyber-source/transient-token';
import {
  CyberSourceCcChangeEvent,
  CyberSourceSecurityCodeChangeEvent
} from '@app/payment/components/cybersource/cyber-source-add-new-cc/cyber-source.model';
import {LAUNCH_CALLER, LaunchDialogService} from '@spartacus/storefront';
import {ConsumerAuthenticationInformationSetup} from '@app/model/cyber-source/payer-auth-setup-response';
import {CyberSourceDeviceDataCollectionFormComponent} from '@app/payment/components/cybersource/cyber-source-device-data-collection-form/cyber-source-device-data-collection-form.component';
import jwt_decode from 'jwt-decode';

declare let Flex: any;

@UntilDestroy()
@Component({
  selector: 'dcp-cyber-source-add-new-cc',
  templateUrl: './cyber-source-add-new-cc.component.html',
  styleUrls: ['./cyber-source-add-new-cc.component.scss'],
})
export class CyberSourceAddNewCcComponent implements OnInit, OnDestroy {

  microform: any = null;
  expirationDate = '';
  isPaymentElementLoaded = false;
  isSaveCardInProgress = false;
  infoMessage: string = null;
  errorMessage: string = null;
  deviceDataCollectionSetup: ConsumerAuthenticationInformationSetup;
  deviceDataCollectionHandler: any;
  threeDsResponseHandler: any;
  authEnrollmentResponse: PayerAuthEnrollmentResponse;
  isOpen = false;
  isSecurityCodeValid = false;
  ccNumber: any;
  securityCode: any;
  transientTokenRaw: string;
  errorMessages: any = {
    ccNumber: {
      text: undefined,
      show: false,
    },
    expirationDate: {
      text: undefined,
      show: false,
    },
    securityCode: {
      text: undefined,
      show: false,
    },
  };

  latestCcNumberEvent: CyberSourceCcChangeEvent;
  latestSecurityCodeEvent: CyberSourceSecurityCodeChangeEvent;

  readonly ERROR_KEY = {
    CC_NUMBER: {
      INCOMPLETE: 'checkout.digitalNativeCheckout.paymentType.cc.errors.cyberSource.ccNumber.incomplete',
      INVALID: 'checkout.digitalNativeCheckout.paymentType.cc.errors.cyberSource.ccNumber.invalid',
    },
    EXPIRATION_DATE: {
      INCOMPLETE: 'checkout.digitalNativeCheckout.paymentType.cc.errors.cyberSource.expirationDate.incomplete',
      IN_THE_PAST: 'checkout.digitalNativeCheckout.paymentType.cc.errors.cyberSource.expirationDate.inThePast',
      MONTH_INVALID: 'checkout.digitalNativeCheckout.paymentType.cc.errors.cyberSource.expirationDate.monthInvalid',
    },
    SECURITY_CODE: {
      INCOMPLETE: 'checkout.digitalNativeCheckout.paymentType.cc.errors.cyberSource.securityCode.incomplete',
    },
    AUTHENTICATION_FAILURE: 'checkout.digitalNativeCheckout.paymentType.cc.errors.cyberSource.authenticationFailed',
  };

  allAllowedCardBrands: string[] = [];
  allowedCardBrands: string[] = [];
  cardImages = {
    visa: '/assets/images/creditcard/visa.png',
    mastercard: '/assets/images/creditcard/mastercard.png',
    amex: '/assets/images/creditcard/amex.png',
    maestro: '/assets/images/creditcard/maestro.png',
    discover: '/assets/images/creditcard//discover.png',
    dinersclub: '/assets/images/creditcard/dinersclub.png',
    jcb: '/assets/images/creditcard/jcb.png'
  };

  readonly CARD_TYPES_MAP = {
    '001': 'visa',
    '002': 'mc',
    '003': 'amex',
  };

  @Input() set sessionRefreshed(msg: string) {
    if (msg) {
      this.translationService.translate(msg).pipe(take(1), untilDestroyed(this)).subscribe(msg => {
        this.errorMessage = msg;
      })
    }
  }

  @Input() isClosable = false;
  @ViewChild(CyberSourceDeviceDataCollectionFormComponent) deviceDataCollectionForm?: CyberSourceDeviceDataCollectionFormComponent;
  @ViewChild('expirationDateInput') expirationDateInput: ElementRef;

  constructor(@Inject(PLATFORM_ID) private platformId: string,
              private cyberSourceService: CyberSourceService,
              private activeCartFacade: ActiveCartFacade,
              private checkoutPaymentService: DcpCheckoutPaymentService,
              private cdr: ChangeDetectorRef,
              private scriptInjectionService: ScriptInjectionService,
              private configService: ConfigService,
              private dcpCheckoutPaymentService: DcpCheckoutPaymentService,
              private userAccountFacade: UserAccountFacade,
              private translationService: TranslationService,
              private renderer: Renderer2,
              private launchDialogService: LaunchDialogService,
              private dcpCcPspService: DcpCcPspService,
              private dcpGlobalMessageService: DcpGlobalMessageService,
  ) {
  }

  ngOnInit() {
    this.initializeFlexMicroform();
    this.listenToDeviceDataCollection();
    this.listenToThreeDsResult();
    this.setInitialCardImage();
    this.listenToIsOpen();
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('message', this.deviceDataCollectionHandler);
      window.removeEventListener('message', this.threeDsResponseHandler);
      this.cyberSourceService.stopRefreshTimer();
    }
  }

  getCardImageAsBackground(): string {
    return this.allowedCardBrands.map(brand => `url(${this.cardImages[brand]})`).join(', ')
  }

  decodeJwtResponse<T>(jwt: string): T {
    const jwtDecoded = jwt_decode<{ aud: string; body: string; iat: string; jti: string }>(jwt);
    return jwtDecoded as T;
  }

  private loadCyberSourceScript(captureContext: CaptureContext): Promise<void> {
    if (isPlatformBrowser(this.platformId) && document.querySelector('head script[id="cyberSource"]')) {
      return Promise.resolve();
    }

    const jwt = this.decodeJwtResponse<any>(captureContext.data);

    const cyberSourceJS: DcpScriptInjection = {
      id: 'cyberSource',
      url: this.configService.getString('cyberSourceScript.url'),
      attributes: {
        integrity: jwt.ctx[0].data.clientLibraryIntegrity,
        crossorigin: 'anonymous'
      },
      type: null,
      target: 'head'
    };

    return this.scriptInjectionService.injectExternalScript(cyberSourceJS);
  }

  private listenToIsOpen() {
    this.cyberSourceService.isAddNewCreditCardComponentOpened().pipe(untilDestroyed(this)).subscribe(value => {
      this.isOpen = value;
      this.cdr.detectChanges();
      if (!this.isOpen) {
        this.dcpCcPspService.setErrorMessageStatus(false);
        this.ccNumber?.clear();
        this.securityCode?.clear();
        this.expirationDate = '';
      }
    });
  }

  private setInitialCardImage() {
    this.activeCartFacade.getActive().pipe(
      take(1),
      switchMap(cart => {
        const {vendor, salesOrg} = this.checkoutPaymentService.getVendorSalesOrg(cart);
        return this.checkoutPaymentService.getCardConfig(vendor, salesOrg, PaymentProvider.CYBERSOURCE);
      }),
      untilDestroyed(this),
    ).subscribe(cardConfig => {
      this.allAllowedCardBrands = cardConfig.allowedCardBrands;
      this.allowedCardBrands = cardConfig.allowedCardBrands;
    });
  }

  // Initialization of the CyberSource Flex Microform
  initializeFlexMicroform() {
    // Step 1: Generate the CyberSource Capture Context
    // The capture context is a digitally signed JWT that provides authentication, one-time keys, and the target origin to the Microform Integration application.
    // Source: https://developer.cybersource.com/docs/cybs/en-us/digital-accept-flex/developer/all/rest/digital-accept-flex/microform-integ-v2/microform-integ-getting-started-v2.html

    this.activeCartFacade.getActive().pipe(
      take(1),
      untilDestroyed(this)
    ).subscribe((cart: Cart) => {
      const {vendor, salesOrg} = this.checkoutPaymentService.getVendorSalesOrg(cart);
      this.cyberSourceService.getCaptureContext(vendor, salesOrg, cart.totalPrice.currencyIso).pipe(
        catchError(error => {
          this.errorMessage = error;
          return EMPTY;
        }),
        untilDestroyed(this)
      ).subscribe((result: CaptureContext) => {
        // Check if the capture context is generated
        if (result.response.status === 201 && result.data !== '') {
          const captureContext = result.data;

          this.loadCyberSourceScript(result).then(_ => {
            // Step 2: Validating the Capture Context
            // The purpose is to ensure the validity of the JWT and confirm that it comes from CyberSource.
            this.cyberSourceService.validateCaptureContext(captureContext).pipe(untilDestroyed(this)).subscribe(valid => {
              if (valid) {
                // Step 3: Setup the CyberSource Flex Microform
                this.setupFlexMicroform(captureContext);
              } else {
                // Handle error when the capture context is invalid
                this.errorMessage = 'Validation of Capture Context failed';
              }
            });
          });

        } else {
          this.errorMessage = 'Could not get Capture Context';
        }
      });
    });
  }

  setupFlexMicroform(captureContext: string): void {
    // Instance for the Flex Microform JS Library
    const myStyles = {
      input: {
        'font-size': '1rem',
        'font-family': 'BentonSans',
      },
      ':focus': {
        color: '#222222',
      },
      ':disabled': {cursor: 'not-allowed'},
      valid: {
        color: '#2175fd',
      },
      invalid: {
        color: '#db2a46',
      },
    };

    try {
      this.cyberSourceService.startRefreshTimer();
      const flex = new Flex(captureContext);
      this.microform = flex.microform('card', {styles: myStyles}); // Using custom styles

      // Create number and security code fields
      this.ccNumber = this.microform.createField('number', {placeholder: '1234 1234 1234 1234'});
      this.securityCode = this.microform.createField('securityCode', {placeholder: 'CVC'});

      // Load the CyberSource Flex Microform input fields
      this.ccNumber.load('#number-container');
      this.securityCode.load('#securityCode-container');

      // Update credit card provider image and security code placeholder to match the detected card type's terminology
      this.ccNumber.on('change', (event: CyberSourceCcChangeEvent) => {
        this.latestCcNumberEvent = event;
        if (event.card && event.card.length > 0) {
          // Show matching card image
          this.allowedCardBrands = [this.cardImages[event.card[0].name]];

          // Update the security code placeholder to match the detected card's naming convention
          this.securityCode.update({placeholder: event.card[0].securityCode.name});

          this.validateCcNumber(event);
          this.cdr.detectChanges();
        } else {
          // Show a generic card image
          this.allowedCardBrands = this.allAllowedCardBrands;
        }
      });

      this.ccNumber.on('blur', () => {
        this.errorMessages.ccNumber.show = true;
        this.validateCcNumber(this.latestCcNumberEvent);
      });

      this.securityCode.on('blur', () => {
        this.errorMessages.securityCode.show = true;
        this.validateSecurityCode(this.latestSecurityCodeEvent);
      });

      // Show form when number and security code is loaded
      this.ccNumber.on('load', () => {
        this.securityCode.on('load', () => {
          this.isPaymentElementLoaded = true;
          this.cdr.detectChanges();
          this.securityCode.on('change', (event: CyberSourceSecurityCodeChangeEvent) => {
            this.latestSecurityCodeEvent = event;
            this.isSecurityCodeValid = event.valid;

            this.validateSecurityCode(event);
            this.cdr.detectChanges();
          });

        });
      });
    } catch (error) {
      this.dcpGlobalMessageService.addMessage(error, this?.constructor?.name, 'errorLogging');
      this.errorMessage = 'Error during flexform initialization';
    }
  }

  submitCreditCard() {
    this.isSaveCardInProgress = true;
    this.errorMessage = null;
    this.infoMessage = null;

    const options = {
      expirationMonth: this.expirationDate.substring(0, 2),
      expirationYear: '20' + this.expirationDate.substring(3, 5)
    };

    // Step 4: Create the transient token and store it in the global this.transientToken
    this.microform.createToken(options, (err, token) => {
      if (err) {
        this.dcpGlobalMessageService.addMessage(err, this?.constructor?.name, 'errorLogging');
        this.errorMessage = err.message;
        this.isSaveCardInProgress = false;
        this.cdr.detectChanges();
      } else {
        this.transientTokenRaw = token;
        this.activeCartFacade.getActive().pipe(
          take(1),
          switchMap(cart => {
            const {vendor, salesOrg} = this.checkoutPaymentService.getVendorSalesOrg(cart);
            return this.checkoutPaymentService.getCardConfig(vendor, salesOrg, PaymentProvider.CYBERSOURCE);
          }),
          untilDestroyed(this),
        ).subscribe(cardConfig => {
          const transientToken = this.jwtToTransientToken(token);
          if (cardConfig['3DSEnable']) {
            this.collectDeviceData(options.expirationMonth, options.expirationYear, transientToken);
          } else {
            this.cyberSourceService.getPayerAuthEnrollment(transientToken.jti).pipe(untilDestroyed(this)).subscribe({
              next: response => {
                this.handlePayerAuthEnrollmentResponse(response);
              },
              error: _ => {
                this.setFormErrorMessage('common.errors.somethingWrong');
              }
            });
          }
        });
      }
    });
  }

  private jwtToTransientToken(jwt: string): TransientToken {
    const [, jwtPayload]: string[] = jwt.split('.');
    return JSON.parse(atob(jwtPayload));
  }

  collectDeviceData(expirationMonth: string, expirationYear: string, transientToken: TransientToken) {
    // Starting the Payer Authentication process
    // Step 5: Setup payer authentication (3D Secure) & Step 2: Device Data Collection
    // Source: https://developer.cybersource.com/docs/cybs/en-us/payer-authentication/developer/all/rest/payer-auth/pa2-ccdc-ddc-intro.html

    this.cyberSourceService.getPayerAuthSetup(expirationMonth, expirationYear, transientToken.jti).pipe(untilDestroyed(this)).subscribe({
      next: (response) => {
        this.deviceDataCollectionSetup = response.consumerAuthenticationInformation;
        this.cdr.detectChanges();
        this.deviceDataCollectionForm.submit();
      },
      error: _ => {
        this.setFormErrorMessage('common.errors.somethingWrong');
        this.isSaveCardInProgress = false;
      },
    });
  }

  setFormErrorMessage(messageKey: string) {
    this.translationService.translate(messageKey).pipe(untilDestroyed(this)).subscribe(msg => {
      this.errorMessage = msg;
      this.cdr.detectChanges();
    });
  }

  private listenToDeviceDataCollection() {
    if (isPlatformBrowser(this.platformId)) {

      this.deviceDataCollectionHandler = async (event) => {
        // Watch for Receiving the Device Data Collection URL Response.
        // Once event is received merchant can proceed to PA Enrollment request.
        if (!!this.deviceDataCollectionSetup && this.deviceDataCollectionSetup.deviceDataCollectionUrl?.includes(event.origin)) {
          // Step 6: Payer Authentication Enrollment
          // Documentation: https://developer.cybersource.com/docs/cybs/en-us/payer-authentication/developer/all/rest/payer-auth/pa2-ccdc-enroll-intro.html
          const transientToken = this.jwtToTransientToken(this.transientTokenRaw);
          this.cyberSourceService.getPayerAuthEnrollment(transientToken.jti, this.deviceDataCollectionSetup.referenceId).pipe(untilDestroyed(this)).subscribe({
            next: response => {
              this.handlePayerAuthEnrollmentResponse(response);
            },
            error: _ => {
              this.isSaveCardInProgress = false;
              this.setFormErrorMessage('common.errors.somethingWrong');
            }
          });
        }
      };

      window.addEventListener('message', this.deviceDataCollectionHandler);
    }
  }

  private handlePayerAuthEnrollmentResponse(response: PayerAuthEnrollmentResponse) {
    // Step 6b: Combined call of Payer Authentication Enrollment and Payment Authorization with Token Creation
    // Documentation: https://developer.cybersource.com/docs/cybs/en-us/payer-authentication/developer/all/rest/payer-auth/pa2-ccdc-enroll-combining-services/pa2_ccdc_combining_services_enroll.html
    // Token Creation only run if no challenge is needed

    this.authEnrollmentResponse = response;
    const paymentResponse = response.paymentResponse[0].value;
    if (paymentResponse.status === 'AUTHORIZED' || paymentResponse.status === 'AUTHENTICATION_SUCCESSFUL') {
      this.saveCreditCard(response);
    } else if (paymentResponse.status === 'PENDING_AUTHENTICATION') {
      // Step 7: Step-Up Iframe
      // Initiate step-up authentication on the front end. This step is only for step-up authentication when the issuing bank wants to challenge the cardholder.
      // Documentation: https://developer.cybersource.com/docs/cybs/en-us/payer-authentication/developer/all/rest/payer-auth/pa2-ccdc-stepup-frame-intro.html
      this.activeCartFacade.getActive().pipe(take(1), untilDestroyed(this)).subscribe((cart) => {
        this.launchDialogService.openDialogAndSubscribe(LAUNCH_CALLER.CYBERSOURCE_3DS_IFRAME, null, {
          consumerAuthenticationInformation: paymentResponse.consumerAuthenticationInformation,
          cartId: cart.code,
        });
      });
    } else if (paymentResponse.status === 'DECLINED') {
      // Failed authentication
      this.errorMessage = paymentResponse?.errorInformation?.message;
      this.isSaveCardInProgress = false;
      this.cdr.detectChanges();
    } else {
      // fallback
      this.setFormErrorMessage('common.errors.somethingWrong');
      this.isSaveCardInProgress = false;
      this.cdr.detectChanges();
    }
  }

  private listenToThreeDsResult() {
    this.threeDsResponseHandler = (event) => {
      if (event.data?.type === '3DSComplete') {

        if (event.data.cancelled) {
          this.closeThreeDs(false);
          return;
        } else {
          this.closeThreeDs(true);
        }

        const transientToken = this.jwtToTransientToken(this.transientTokenRaw);
        this.cyberSourceService.getPayerAuthEnrollment(transientToken.jti, this.deviceDataCollectionSetup.referenceId, event.data.transactionId).pipe(untilDestroyed(this)).subscribe({
            next: result => {
              const authEnrollmentResponse = result.paymentResponse[0].value;
              if (authEnrollmentResponse.status === 'AUTHORIZED') {
                this.saveCreditCard(result);
              } else if (authEnrollmentResponse.errorInformation?.reason === 'CONSUMER_AUTHENTICATION_FAILED') {
                this.setFormErrorMessage(this.ERROR_KEY.AUTHENTICATION_FAILURE);
              } else {
                this.setFormErrorMessage('common.errors.somethingWrong');
              }
              this.isSaveCardInProgress = false;
              this.cdr.detectChanges();
            },
            error: _ => {
              // fallback
              this.isSaveCardInProgress = false;
              this.setFormErrorMessage('common.errors.somethingWrong');
            }
          },
        );
      }
    };
    window.addEventListener('message', this.threeDsResponseHandler);
  }

  closeThreeDs(saveCardInProgress = false): void {
    this.isSaveCardInProgress = saveCardInProgress;
    this.launchDialogService.closeDialog('3DS complete');
    this.cdr.detectChanges();
  }

  saveCreditCard(data: PayerAuthEnrollmentResponse): void {
    this.userAccountFacade.get().pipe(
      take(1),
      switchMap(user => {
        const transientToken = this.jwtToTransientToken(this.transientTokenRaw);
        const paymentDetails = this.preparePaymentDetails(user, data, transientToken);
        return this.dcpCheckoutPaymentService.createFullCreditCardDetails(paymentDetails);
      }),
      untilDestroyed(this)
    ).subscribe({
      next: result => {
        this.dcpCcPspService.setErrorMessageStatus(false);
        this.isSaveCardInProgress = false;
        this.cdr.detectChanges();
        this.close();
      },
      error: _ => {
        this.isSaveCardInProgress = false;
        this.setFormErrorMessage('common.errors.somethingWrong');
      }
    });
  }

  preparePaymentDetails(user: User, payerAuthEnrollment: PayerAuthEnrollmentResponse, transientToken: TransientToken): PaymentDetails {
    const cardType = payerAuthEnrollment.paymentResponse[0].value.paymentInformation.card.type;
    const ccCode = this.CARD_TYPES_MAP[cardType] || cardType.toLowerCase();
    const paymentDetails = this.dcpCheckoutPaymentService.prepareBillingAddress(user);
    return {
      ...paymentDetails,
      cardNumber: transientToken.content.paymentInformation.card.number.maskedValue,
      cardType: {
        code: ccCode,
      },
      cvn: null,
      defaultPayment: true,
      expiryMonth: transientToken.content.paymentInformation.card.expirationMonth.value,
      expiryYear: transientToken.content.paymentInformation.card.expirationYear.value,
      saved: true,
      subscriptionId: payerAuthEnrollment.paymentResponse[0].value.tokenInformation.customer.id,
      paymentProvider: PaymentProvider.CYBERSOURCE,
    };
  }

  close() {
    this.errorMessages.securityCode.text = null;
    this.errorMessages.securityCode.show = false;
    this.errorMessages.ccNumber.text = null;
    this.errorMessages.ccNumber.show = false;
    this.errorMessages.expirationDate.text = null;
    this.errorMessages.expirationDate.show = false;
    this.errorMessage = null;
    this.cyberSourceService.closeAddNewCreditCardComponent();
  }

  validateCcNumber(event: CyberSourceCcChangeEvent) {
    if (!this.errorMessages.ccNumber.show) {
      return;
    }

    if (!event) {
      this.setErrorMessage(this.ERROR_KEY.CC_NUMBER.INCOMPLETE, 'ccNumber');
      return;
    }

    if (!event.couldBeValid && !event.valid) {
      // ccNumber is red, it is invalid
      this.setErrorMessage(this.ERROR_KEY.CC_NUMBER.INVALID, 'ccNumber');
    } else if (event.couldBeValid && !event.valid) {
      // ccNumber is black, it is incomplete
      this.setErrorMessage(this.ERROR_KEY.CC_NUMBER.INCOMPLETE, 'ccNumber');
    } else {
      // ccNumber is blue, it is valid
      this.errorMessages.ccNumber.text = null;
    }
  }

  validateSecurityCode(event: CyberSourceSecurityCodeChangeEvent) {
    if (!this.errorMessages.securityCode.show) {
      return;
    }

    if (!event || !event.valid) {
      // securityCode is incomplete
      this.setErrorMessage(this.ERROR_KEY.SECURITY_CODE.INCOMPLETE, 'securityCode');
    } else {
      this.errorMessages.securityCode.text = null;
    }
  }

  expirationDateUpdate(date: string) {
    const digitsOnly = date.replace(/\D/g, '');
    if (digitsOnly.length >= 1 && Number.parseInt(digitsOnly[0]) > 1) {
      // Adds 0 if first digit is of month > 1
      this.expirationDateUpdate('0' + digitsOnly);
      return;
    }
    if (digitsOnly.length < 2) {
      // When less then 2 digits leave as it is
      this.expirationDate = digitsOnly;
    } else {
      // Adds '/' to date
      const formattedDate = this.insertCharAt(digitsOnly, 2, '/').slice(0, 5);
      this.expirationDate = formattedDate;
    }
    this.renderer.setProperty(this.expirationDateInput.nativeElement, 'value', this.expirationDate);

    this.validateExpirationDate();
  }

  handleBackSpace(event) {
    const inputElement = this.expirationDateInput.nativeElement;
    const start = inputElement.selectionStart;
    const end = inputElement.selectionEnd;

    if (start === end && start > 0 && inputElement.value[start - 1] === '/') {
      // When input is e.g '05/' and caret is just after '/'

      // Prevent the default backspace action
      event.preventDefault();

      // Set the new value and update the model
      const newValue = inputElement.value.slice(0, start - 2) + inputElement.value.slice(start);
      this.expirationDateUpdate(newValue);
    }
  }

  insertCharAt(str, index, char): string {
    return str.slice(0, index) + char + str.slice(index);
  }

  isFormComplete(): boolean {
    return this.isCreditCardNumberValid() && this.isExpirationDateValid() && this.isSecurityCodeValid;
  }

  isCreditCardNumberValid(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      return !!document.querySelector('#number-container.flex-microform-valid');
    }
  }

  isExpirationDateValid(): boolean {
    return this.isExpirationDateFormatValid()
      && this.isExpirationMonthValid()
      && !this.isExpirationDateInPast();
  }

  /**
   * checks if entered expiration date is XX/YY
   */
  isExpirationDateFormatValid(): boolean {
    return this.expirationDate.length === 5
      && !Number.isNaN(this.expirationDate.substring(0, 2))
      && !Number.isNaN(this.expirationDate.substring(3, 5));
  }

  validateExpirationDate() {
    if (this.errorMessages.expirationDate.show || this.expirationDate.length === 5) {
      // when all digits are inserted or customer lost focus on the field
      if (this.expirationDate.length < 5) {
        this.setErrorMessage(this.ERROR_KEY.EXPIRATION_DATE.INCOMPLETE, 'expirationDate');
      } else if (!this.isExpirationMonthValid()) {
        this.setErrorMessage(this.ERROR_KEY.EXPIRATION_DATE.MONTH_INVALID, 'expirationDate');
      } else if (this.isExpirationDateInPast()) {
        this.setErrorMessage(this.ERROR_KEY.EXPIRATION_DATE.IN_THE_PAST, 'expirationDate');
      } else {
        this.errorMessages.expirationDate.text = null;
      }
    } else if (this.expirationDate.length >= 2 && !this.isExpirationMonthValid()) {
      // when customer already inserted first 2 digits and month is invalid
      this.setErrorMessage(this.ERROR_KEY.EXPIRATION_DATE.MONTH_INVALID, 'expirationDate');
    } else {
      this.errorMessages.expirationDate.text = null;
    }
  }

  setErrorMessage(key: string, field: 'ccNumber' | 'expirationDate' | 'securityCode'): void {
    this.translationService.translate(key).pipe(
      take(1),
      untilDestroyed(this)
    ).subscribe((errorMessage: string) => {
      this.errorMessages[field].text = errorMessage;
      this.cdr.detectChanges();
    });
  }

  isExpirationDateInPast(): boolean {
    const month = Number.parseInt(this.expirationDate.substring(0, 2));
    const year = 2000 + Number.parseInt(this.expirationDate.substring(3, 5));
    const currentDate = new Date();
    if (currentDate.getFullYear() > year) {
      return true;
    }
    if (currentDate.getFullYear() === year && (currentDate.getMonth() + 1) > month) {
      return true;
    }
    return false;
  }

  isExpirationMonthValid(): boolean {
    const month = Number.parseInt(this.expirationDate.substring(0, 2));
    return month >= 1 && month <= 12;
  }
}
